import ComponentManager, {
	ComponentOptions,
	ComponentOptionsInterface,
	ComponentOptionsValuePlaceholder
} from "../../../componentFramework";
import EventListenerManager from "../../EventListenerManager";
import InlineMenuManager from "../InlineMenuManager";
import {InlineMenuControl} from "../index";
import eventListenerOption from "../../../scripts/eventListenerOption";

const defaultOptions = {
	styleClass: {
		isOpen: "inlineMenu--isOpen",
	},
	id: ComponentOptionsValuePlaceholder.string.required, // Falls back to this.rootElement.id if data-inlineMenu-id is not set.
	viewGutter: 10
};
export type DefaultOptionsType = typeof defaultOptions;

/**
 * Use an instance of InlineMenuControl to open a menu. Or use the methods on InlineMenuManager
 */
@ComponentManager.register()
export default class InlineMenu<InstanceOptions extends ComponentOptionsInterface = ComponentOptionsInterface> {
	public static componentStyleClass = "inlineMenu";

	protected readonly options: ComponentOptions<DefaultOptionsType & InstanceOptions>;
	protected readonly eventListenerManager = new EventListenerManager();

	constructor(private readonly rootElement: HTMLElement, instanceOptions?: InstanceOptions) {
		this.options = new ComponentOptions(
			this,
			rootElement,
			defaultOptions,
			this.rootElement.id.length !== 0 ? {id: this.rootElement.id} : undefined // Use the id attribute if data-inlineMenu-id is not set
		).extend(instanceOptions);
		const inlineMenuManager = InlineMenuManager.getInstance();

		// Stop clicks from propagating past the rootElement
		this.eventListenerManager.manage(rootElement).addEventListener("click", (event: MouseEvent) => {
			event.stopPropagation();
		}, eventListenerOption.passiveNotCapture);

		// Register this InlineMenu
		inlineMenuManager.registerMenu(this, rootElement, this.doOpen, this.doClose);
	}


	@ComponentManager.on("removed")
	protected componentRemoved() {
		InlineMenuManager.getInstance().unregisterMenu(this);
		this.eventListenerManager.removeListeners();
	}

	public getId(): string {
		return this.options.getValues().id;
	}

	public getRootElement() {
		return this.rootElement;
	}

	private doOpen(control: InlineMenuControl) {
		this.updatePosition(control);
		this.rootElement.classList.add(this.options.getValues().styleClass.isOpen);
	}
	private doClose() {
		this.rootElement.classList.remove(this.options.getValues().styleClass.isOpen);
	}

	private updatePosition(control: InlineMenuControl) {
		const gutter = this.options.getValues().viewGutter;
		const docElem = this.rootElement.ownerDocument.documentElement;

		const controlRect = control.getBoundingClientRect();
		const menuRect = this.rootElement.getBoundingClientRect();

		const x = controlRect.left + menuRect.width + gutter > docElem.clientWidth ? controlRect.right - menuRect.width : controlRect.left;
		const y = controlRect.bottom + menuRect.height + gutter > docElem.clientHeight ? controlRect.top - menuRect.height : controlRect.bottom;

		this.rootElement.style.left = x + "px";
		this.rootElement.style.top = y + "px";
	}
}
